import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useDivisions } from '@/hooks/useDivisions';
import type { PathSetting } from '../api';
import { pathSettingLabel } from '../format';
import { usePathSettings, useSetPathSetting } from '../hooks';
import { useVpnPermissions } from '../permissions';
import { Card } from './Facts';

const SETTABLE: PathSetting[] = ['no_preference', 'direct_preferred', 'direct_only'];
const LATER: PathSetting[] = ['relay_preferred', 'relay_only'];
const ORGANIZATIONS = 'organization';

function Choice({
  id,
  value,
  inherit,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  /** For a division: "use the organization's". */
  inherit?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className="w-72">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {inherit ? <SelectItem value={ORGANIZATIONS}>{inherit}</SelectItem> : null}
        {SETTABLE.map((v) => (
          <SelectItem key={v} value={v}>
            {pathSettingLabel(v)}
          </SelectItem>
        ))}
        {LATER.map((v) => (
          <SelectItem key={v} value={v} disabled>
            {pathSettingLabel(v)} · not available yet
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * How sessions reach a site (docs/VpnAccess.md, "Path setting"): the
 * organization's, and each division's or the organization's. Without the relay,
 * every value comes to the same today: a direct path, or no session.
 */
export function PathSettingCard() {
  const { user } = useAuth();
  const { canUpdateVpn, canUpdateOrgVpn } = useVpnPermissions();
  const settings = usePathSettings();
  const divisions = useDivisions(user?.organizationId);
  const set = useSetPathSetting();
  const names = new Map((divisions.data ?? []).map((d) => [d.id, d.name]));

  return (
    <Card
      title="Connection path"
      description="Whether sessions go direct or through Witina's relay. The relay isn't available yet, so sessions go direct, or tell the technician why they can't."
    >
      {settings.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : settings.isError || !settings.data ? (
        <p className="text-xs text-muted-foreground">Couldn&rsquo;t load the connection path.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="path-org">Organization</Label>
            <Choice
              id="path-org"
              value={settings.data.organization}
              disabled={!canUpdateOrgVpn || set.isPending}
              onChange={(v) => {
                set.mutate({ divisionId: null, pathSetting: v as PathSetting });
              }}
            />
          </div>
          {settings.data.divisions.map((d) => (
            <div key={d.divisionId} className="grid gap-1.5">
              <Label htmlFor={`path-${d.divisionId}`}>{names.get(d.divisionId) ?? 'A division'}</Label>
              <Choice
                id={`path-${d.divisionId}`}
                value={d.pathSetting ?? ORGANIZATIONS}
                inherit={`The organization's (${pathSettingLabel(settings.data.organization)})`}
                disabled={!canUpdateVpn || set.isPending}
                onChange={(v) => {
                  set.mutate({ divisionId: d.divisionId, pathSetting: v === ORGANIZATIONS ? null : (v as PathSetting) });
                }}
              />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
